import React, { useState, useRef, useEffect, useMemo } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const Table4 = () => {
  // Load saved name from localStorage or use default
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[4] || 'Transactions');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectionStats, setSelectionStats] = useState(null);

  // Calculate totals for Cross Profit, Expense, and Net Profit
  // Fetch from MySQL first, fallback to localStorage
  const [tableDataForTotals, setTableDataForTotals] = useState(null);
  
  useEffect(() => {
    const fetchDataForTotals = async () => {
      try {
        // Try MySQL first
        const mysqlData = await tableAPI.getById('table4');
        if (mysqlData && mysqlData.length > 1) {
          setTableDataForTotals(mysqlData);
          // Sync to localStorage
          localStorage.setItem('table_table4', JSON.stringify(mysqlData));
        } else {
          // MySQL empty - clear localStorage and set empty
          localStorage.removeItem('table_table4');
          setTableDataForTotals(null);
        }
      } catch (error) {
        // MySQL failed - try localStorage but validate
        const localData = localStorage.getItem('table_table4');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            if (parsed && parsed.length > 1) {
              setTableDataForTotals(parsed);
            } else {
              localStorage.removeItem('table_table4');
              setTableDataForTotals(null);
            }
          } catch (e) {
            localStorage.removeItem('table_table4');
            setTableDataForTotals(null);
          }
        } else {
          setTableDataForTotals(null);
        }
      }
    };
    
    fetchDataForTotals();
    // Refresh every 2 seconds to sync with MySQL
    const interval = setInterval(fetchDataForTotals, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const calculateTotals = () => {
    try {
      // Use fetched data from MySQL
      const parsed = tableDataForTotals;
      // Check if we only have header row or empty rows
      if (!parsed || parsed.length < 2) return { 
        inrTotal: 0, 
        stockInrTotal: 0,
        crossProfit: 0, 
        expense: 0, 
        netProfit: 0, 
        hasData: false 
      };
      
      const headers = parsed[0];
      const inrIndex = headers.indexOf('INR');
      const stockInrIndex = headers.indexOf('Stock INR');
      const crossProfitIndex = headers.indexOf('Cross Profit');
      const expenseIndex = headers.indexOf('Expense');
      const netProfitIndex = headers.indexOf('Net Profit');
      
      let inrTotal = 0;
      let stockInrTotal = 0;
      let crossProfit = 0;
      let expense = 0;
      let netProfit = 0;
      let hasData = false;
      
      parsed.slice(1).forEach(row => {
        if (row && row.length > 0) {
          // Check if row has any meaningful data (not just empty strings)
          const hasRowData = row.some((cell, idx) => {
            if (idx === 0 || idx === inrIndex || idx === stockInrIndex || idx === crossProfitIndex || idx === expenseIndex || idx === netProfitIndex) {
              return cell !== null && cell !== undefined && cell !== '';
            }
            return false;
          });
          
          if (hasRowData) {
            hasData = true;
          }
          
          if (inrIndex !== -1 && row[inrIndex]) {
            const value = parseFloat(row[inrIndex].toString().replace(/[^0-9.-]/g, '')) || 0;
            if (value !== 0) {
              inrTotal += value;
              hasData = true;
            }
          }
          if (stockInrIndex !== -1 && row[stockInrIndex]) {
            const value = parseFloat(row[stockInrIndex].toString().replace(/[^0-9.-]/g, '')) || 0;
            if (value !== 0) {
              stockInrTotal += value;
              hasData = true;
            }
          }
          if (crossProfitIndex !== -1 && row[crossProfitIndex]) {
            const value = parseFloat(row[crossProfitIndex].toString().replace(/[^0-9.-]/g, '')) || 0;
            if (value !== 0) {
              crossProfit += value;
              hasData = true;
            }
          }
          if (expenseIndex !== -1 && row[expenseIndex]) {
            const value = parseFloat(row[expenseIndex].toString().replace(/[^0-9.-]/g, '')) || 0;
            if (value !== 0) {
              expense += value;
              hasData = true;
            }
          }
          if (netProfitIndex !== -1 && row[netProfitIndex]) {
            const value = parseFloat(row[netProfitIndex].toString().replace(/[^0-9.-]/g, '')) || 0;
            if (value !== 0) {
              netProfit += value;
              hasData = true;
            }
          }
        }
      });
      
      return { inrTotal, stockInrTotal, crossProfit, expense, netProfit, hasData };
    } catch (e) {
      console.error('Error calculating totals:', e);
      return { 
        inrTotal: 0, 
        stockInrTotal: 0,
        crossProfit: 0, 
        expense: 0, 
        netProfit: 0, 
        hasData: false 
      };
    }
  };

  const totals = useMemo(() => calculateTotals(), [tableDataForTotals]);

  // Reload totals when table data changes (check for actual changes)
  // DISABLED: Don't reload from localStorage every 500ms - data comes from MySQL
  // This was causing data to disappear after loading from MySQL
  // useEffect(() => {
  //   let lastDataHash = '';
  //   const interval = setInterval(() => {
  //     const currentData = localStorage.getItem('table_table4');
  //     const currentHash = currentData ? JSON.stringify(currentData).slice(0, 1000) : '';
  //     
  //     if (currentHash !== lastDataHash) {
  //       lastDataHash = currentHash;
  //       setRefreshKey(prev => prev + 1);
  //     }
  //   }, 500);
  //   return () => clearInterval(interval);
  // }, []);

  // Sync table name from localStorage when it changes
  useEffect(() => {
    const handleNameUpdate = () => {
      const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = savedNames[4] || 'Transactions';
      setTableName(prevName => {
        if (prevName !== newName) {
          return newName;
        }
        return prevName;
      });
    };
    
    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  // Only headers - ExcelTable will load actual data from MySQL
  const initialData = [
    ['S no', 'Date', 'Code', 'AED', 'Selling Rate', 'INR', 'Buying Rate', 'Stock INR', 'Cross Profit', 'Expense', 'Net Profit', 'Notes']
  ];

  // No currency dropdown column (AED and INR are separate columns)
  const currencyColumns = [];
  // Total columns: INR (5), Stock INR (7), Cross Profit (8), Expense (9), Net Profit (10)
  const totalColumns = [5, 7, 8, 9, 10];

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage for sidebar sync
    const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    savedNames[4] = tableName; // Table4 has id 4
    localStorage.setItem('defaultTableNames', JSON.stringify(savedNames));
    // Dispatch custom event to notify sidebar
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ margin: 0 }}>
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            {totals.hasData && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Stock INR Total:</span>
                  <span style={{ fontSize: '0.875rem', color: '#0891b2', fontWeight: '700' }}>
                    ₹{totals.stockInrTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {selectionStats && selectionStats[7] && selectionStats[7].hasNumeric && (
                    <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Selected: ₹{selectionStats[7].sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[7].average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>INR Total:</span>
                  <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                    ₹{totals.inrTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {selectionStats && selectionStats[5] && selectionStats[5].hasNumeric && (
                    <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Selected: ₹{selectionStats[5].sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[5].average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Cross Profit:</span>
                  <span style={{ fontSize: '0.875rem', color: '#8b5cf6', fontWeight: '700' }}>
                    ₹{totals.crossProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {selectionStats && selectionStats[8] && selectionStats[8].hasNumeric && (
                    <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Selected: ₹{selectionStats[8].sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[8].average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Expense:</span>
                  <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '700' }}>
                    ₹{totals.expense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {selectionStats && selectionStats[9] && selectionStats[9].hasNumeric && (
                    <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Selected: ₹{selectionStats[9].sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[9].average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Net Profit:</span>
                  <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '700' }}>
                    ₹{totals.netProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {selectionStats && selectionStats[10] && selectionStats[10].hasNumeric && (
                    <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Selected: ₹{selectionStats[10].sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[10].average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={currencyColumns}
        fixedHeaders={true}
        totalColumns={totalColumns}
        tableId="table4"
        tableName={tableName}
        onSelectionStatsChange={setSelectionStats}
      />
    </div>
  );
};

export default Table4;

